import { useCallback } from 'react';

import type { Label } from '@/entities/board/types';

import { LabelGrid } from './LabelGrid';
import styles from './LabelChipPicker.module.css';

interface Props {
  labels: readonly Label[];
  selectedIds: readonly string[];
  /** Called with the label and whether it should now be selected. */
  onToggle: (label: Label, selected: boolean) => void;
  /** Accessible name of the group (kept for callers; the grid names itself). */
  label: string;
  /** Shown when the board has no labels. */
  emptyText: string;
  disabled?: boolean;
}

/**
 * Pick labels of a card (card dialog, Quick/Advanced Create, Calendar filter): the shared
 * searchable multi-column grid (`LabelGrid`), so 100 labels stay usable and every screen picks
 * the same way. A selected chip shows a check and a thin ring, never color alone.
 */
export function LabelChipPicker({
  labels,
  selectedIds,
  onToggle,
  emptyText,
  disabled = false,
}: Props) {
  const byId = useCallback(
    (labelId: string) => {
      const item = labels.find((candidate) => candidate.id === labelId);
      if (item) onToggle(item, !selectedIds.includes(labelId));
    },
    [labels, onToggle, selectedIds],
  );
  if (labels.length === 0) return <p className={styles.empty}>{emptyText}</p>;
  return (
    <LabelGrid labels={labels} selectedIds={selectedIds} onToggle={byId} disabled={disabled} />
  );
}
